// Tiptap-based WYSIWYG editor — glitchOS themed.
// Toolbar with formatting + image upload; content stored as HTML string.
import { useEditor, EditorContent, type Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import { common, createLowlight } from "lowlight"
import { useRef, useCallback, useEffect } from "react"
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Quote,
  Minus,
  ImagePlus,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Heading2,
  Heading3,
  FileCode2,
} from "lucide-react"
import { uploadUpdateMedia } from "../lib/supabase"

const lowlight = createLowlight(common)

// ---------------------------------------------------------------------------
// Toolbar button
// ---------------------------------------------------------------------------

function Btn({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors disabled:opacity-20 ${
        active
          ? "bg-white/[0.10] text-white/80"
          : "text-white/35 hover:text-white/60 hover:bg-white/[0.06]"
      }`}
    >
      {children}
    </button>
  )
}

function Sep() {
  return <div className="w-px h-4 bg-white/[0.06] mx-0.5" />
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function Toolbar({ editor, updateId }: { editor: Editor; updateId?: string }) {
  const fileRef = useRef<HTMLInputElement>(null)

  const handleImage = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      try {
        const url = await uploadUpdateMedia(file, updateId)
        editor.chain().focus().setImage({ src: url }).run()
      } catch {
        // silently fail — could add toast later
      }
      e.target.value = ""
    },
    [editor, updateId]
  )

  const addLink = useCallback(() => {
    const prev = editor.getAttributes("link").href ?? ""
    const url = window.prompt("URL", prev)
    if (url === null) return
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
    }
  }, [editor])

  const s = 14

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-white/[0.06]">
      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2">
        <Heading2 size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3">
        <Heading3 size={s} />
      </Btn>
      <Sep />
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
        <Bold size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
        <Italic size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough">
        <Strikethrough size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Inline code">
        <Code size={s} />
      </Btn>
      <Sep />
      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list">
        <List size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list">
        <ListOrdered size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote">
        <Quote size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block">
        <FileCode2 size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider">
        <Minus size={s} />
      </Btn>
      <Sep />
      <Btn onClick={() => fileRef.current?.click()} title="Insert image">
        <ImagePlus size={s} />
      </Btn>
      <Btn onClick={addLink} active={editor.isActive("link")} title="Add link">
        <LinkIcon size={s} />
      </Btn>
      <Sep />
      <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <Undo2 size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <Redo2 size={s} />
      </Btn>
      <input ref={fileRef} type="file" accept="image/*,video/*,.gif" className="hidden" onChange={handleImage} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Editor component
// ---------------------------------------------------------------------------

export function RichTextEditor({
  content,
  onChange,
  updateId,
  placeholder = "Write something…",
}: {
  content: string
  onChange: (html: string) => void
  updateId?: string
  placeholder?: string
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      CodeBlockLowlight.configure({ lowlight }),
      Image.configure({ inline: false, allowBase64: true }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content,
    editorProps: {
      attributes: {
        class: "prose-editor focus:outline-none min-h-[160px] max-h-[50vh] overflow-y-auto px-4 py-3 text-[14px] leading-relaxed text-white/80",
      },
      handleDrop(view, event, _slice, moved) {
        if (moved || !event.dataTransfer?.files.length) return false
        const file = event.dataTransfer.files[0]
        if (!file?.type.startsWith("image/")) return false
        uploadUpdateMedia(file, updateId).then((url) => {
          const { schema } = view.state
          const node = schema.nodes.image.create({ src: url })
          const transaction = view.state.tr.replaceSelectionWith(node)
          view.dispatch(transaction)
        })
        return true
      },
      handlePaste(view, event) {
        const items = event.clipboardData?.items
        if (!items) return false
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            event.preventDefault()
            const file = item.getAsFile()
            if (!file) continue
            uploadUpdateMedia(file, updateId).then((url) => {
              const { schema } = view.state
              const node = schema.nodes.image.create({ src: url })
              const transaction = view.state.tr.replaceSelectionWith(node)
              view.dispatch(transaction)
            })
            return true
          }
        }
        return false
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML())
    },
  })

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false })
    }
  }, [content, editor])

  if (!editor) return null

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] overflow-hidden focus-within:ring-2 focus-within:ring-luster-400/30 transition-all">
      <Toolbar editor={editor} updateId={updateId} />
      <EditorContent editor={editor} />
    </div>
  )
}
